import {
  ActivityItem,
  AnnouncementItem,
  NewsItem,
  SiteSettings,
} from "@/lib/webContentTypes";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:5000/api";
const ADMIN_TOKEN_KEY = "apr_web_admin_token";

export function getWebAdminToken(): string | null {
  return sessionStorage.getItem(ADMIN_TOKEN_KEY);
}

export function setWebAdminToken(token: string): void {
  sessionStorage.setItem(ADMIN_TOKEN_KEY, token);
}

export function clearWebAdminToken(): void {
  sessionStorage.removeItem(ADMIN_TOKEN_KEY);
}

export async function webAdminLogin(password: string): Promise<{ token: string; expiresInSeconds: number }> {
  return request<{ token: string; expiresInSeconds: number }>("/web-content/admin/auth/login", {
    method: "POST",
    body: JSON.stringify({ password }),
  });
}

export async function webAdminChangePassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
  return request<{ message: string }>("/web-content/admin/auth/change-password", {
    method: "POST",
    auth: true,
    body: JSON.stringify({ currentPassword, newPassword }),
  });
}

export async function getPublicSiteSettings(): Promise<SiteSettings> {
  return request<SiteSettings>("/web-content/public/settings");
}

export async function getPublicNews(limit?: number): Promise<NewsItem[]> {
  const suffix = typeof limit === "number" ? `?limit=${limit}` : "";
  return request<NewsItem[]>(`/web-content/public/news${suffix}`);
}

export async function getPublicNewsById(id: string): Promise<NewsItem> {
  return request<NewsItem>(`/web-content/public/news/${id}`);
}

export async function getPublicActivities(limit?: number): Promise<ActivityItem[]> {
  const suffix = typeof limit === "number" ? `?limit=${limit}` : "";
  return request<ActivityItem[]>(`/web-content/public/activities${suffix}`);
}

export async function getPublicAnnouncements(limit?: number): Promise<AnnouncementItem[]> {
  const suffix = typeof limit === "number" ? `?limit=${limit}` : "";
  return request<AnnouncementItem[]>(`/web-content/public/announcements${suffix}`);
}

export async function getAdminSettings(): Promise<SiteSettings> {
  return request<SiteSettings>("/web-content/admin/settings", { auth: true });
}

export async function updateAdminSettings(input: Partial<SiteSettings>): Promise<SiteSettings> {
  return request<SiteSettings>("/web-content/admin/settings", {
    method: "PATCH",
    auth: true,
    body: JSON.stringify(input),
  });
}

export async function getAdminNews(): Promise<NewsItem[]> {
  return request<NewsItem[]>("/web-content/admin/news", { auth: true });
}

export async function createAdminNews(input: Partial<NewsItem>): Promise<NewsItem> {
  return request<NewsItem>("/web-content/admin/news", {
    method: "POST",
    auth: true,
    body: JSON.stringify({
      title: input.title,
      category: input.category,
      summary: input.summary,
      content: input.content,
      imageUrl: input.imageUrl,
      publishedAt: input.date,
      active: input.active,
    }),
  });
}

export async function updateAdminNews(id: string, input: Partial<NewsItem>): Promise<NewsItem> {
  return request<NewsItem>(`/web-content/admin/news/${id}`, {
    method: "PATCH",
    auth: true,
    body: JSON.stringify({
      title: input.title,
      category: input.category,
      summary: input.summary,
      content: input.content,
      imageUrl: input.imageUrl,
      publishedAt: input.date,
      active: input.active,
    }),
  });
}

export async function deleteAdminNews(id: string): Promise<void> {
  await request(`/web-content/admin/news/${id}`, {
    method: "DELETE",
    auth: true,
  });
}

export async function getAdminActivities(): Promise<ActivityItem[]> {
  return request<ActivityItem[]>("/web-content/admin/activities", { auth: true });
}

export async function createAdminActivities(input: Partial<ActivityItem>): Promise<ActivityItem> {
  return request<ActivityItem>("/web-content/admin/activities", {
    method: "POST",
    auth: true,
    body: JSON.stringify({
      title: input.title,
      summary: input.summary,
      content: input.content,
      location: input.location,
      imageUrl: input.imageUrl,
      publishedAt: input.date,
      active: input.active,
    }),
  });
}

export async function updateAdminActivities(id: string, input: Partial<ActivityItem>): Promise<ActivityItem> {
  return request<ActivityItem>(`/web-content/admin/activities/${id}`, {
    method: "PATCH",
    auth: true,
    body: JSON.stringify({
      title: input.title,
      summary: input.summary,
      content: input.content,
      location: input.location,
      imageUrl: input.imageUrl,
      publishedAt: input.date,
      active: input.active,
    }),
  });
}

export async function deleteAdminActivities(id: string): Promise<void> {
  await request(`/web-content/admin/activities/${id}`, {
    method: "DELETE",
    auth: true,
  });
}

export async function getAdminAnnouncements(): Promise<AnnouncementItem[]> {
  return request<AnnouncementItem[]>("/web-content/admin/announcements", { auth: true });
}

export async function createAdminAnnouncements(input: Partial<AnnouncementItem>): Promise<AnnouncementItem> {
  return request<AnnouncementItem>("/web-content/admin/announcements", {
    method: "POST",
    auth: true,
    body: JSON.stringify({
      title: input.title,
      summary: input.summary,
      content: input.content,
      announcementType: input.announcementType,
      publishedAt: input.date,
      active: input.active,
    }),
  });
}

export async function updateAdminAnnouncements(id: string, input: Partial<AnnouncementItem>): Promise<AnnouncementItem> {
  return request<AnnouncementItem>(`/web-content/admin/announcements/${id}`, {
    method: "PATCH",
    auth: true,
    body: JSON.stringify({
      title: input.title,
      summary: input.summary,
      content: input.content,
      announcementType: input.announcementType,
      publishedAt: input.date,
      active: input.active,
    }),
  });
}

export async function deleteAdminAnnouncements(id: string): Promise<void> {
  await request(`/web-content/admin/announcements/${id}`, {
    method: "DELETE",
    auth: true,
  });
}

type RequestOptions = {
  method?: string;
  auth?: boolean;
  body?: string;
};

async function request<T = unknown>(path: string, options?: RequestOptions): Promise<T> {
  const headers: Record<string, string> = {
    "content-type": "application/json",
  };

  if (options?.auth) {
    const token = getWebAdminToken();
    if (!token) throw new Error("Sesion expirada");
    headers.authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${path}`, {
    method: options?.method ?? "GET",
    headers,
    body: options?.body,
  });

  if (!response.ok) {
    if (response.status === 401 && options?.auth) {
      clearWebAdminToken();
    }

    const message = await parseErrorMessage(response);
    throw new Error(message ?? `Error ${response.status}`);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

async function parseErrorMessage(response: Response): Promise<string | null> {
  const payload = (await response.json().catch(() => null)) as Record<string, unknown> | null;
  if (!payload) return null;

  if (typeof payload.message === "string") return payload.message;
  if (Array.isArray(payload.message) && typeof payload.message[0] === "string") {
    return String(payload.message[0]);
  }

  return null;
}
